import { useAdminContext } from '@/contexts/AdminContext';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import {
    AGENT_TYPE,
    agentService,
    EVENT_TYPES,
    FileItem,
    ROLES,
    STATUSES,
    UserMessage
} from '@common/api/agentService';
import { ContextItem } from '@common/components/AgentInput';
import {
    useCallback, useEffect, useRef
} from 'react';
import {
    useNavigate,
    useParams,
    useSearchParams
} from 'react-router-dom';

export const useChatSearch = () => {
    const {
        chat: {
            addMessage,
            setCurrentConversationId,
            currentConversationId,
            setHistory,
            setMessages,
            setIsGenerating,
            cancelCurrentRequest,
            createNewAbortController,
        }
    } = useAdminContext();
    const {profile} = useAuth();
    const { modal } = useAdminContext();
    const navigate = useNavigate();

    const chatSideBarScrollRef = useRef<HTMLDivElement>(null);

    const {user, openSignInModal} = useAuth();
    const [searchParams] = useSearchParams();
    const inputValue = searchParams.get('inputval');
    const inputFiles = searchParams.get('inputFiles');
    const { showToast } = useToast();
    const { pulseId} = useParams()

    const _handleSearch = useCallback(async (
        inputValue: string, 
        uploadedFiles:FileItem[],
        context?: ContextItem[],
        isRetry?: boolean,
    ) => {
        // 立即创建 AbortController，确保取消功能始终可用
        const controller = createNewAbortController();
        const signal = controller.signal;

        if (!user) {
            openSignInModal();
            return;
        };

        if (!inputValue && uploadedFiles.length === 0) return;


        if (chatSideBarScrollRef.current) {
            chatSideBarScrollRef.current.scrollTop = 0;
        }

        // 添加用户消息到本地状态
        addMessage({
            conversation_id: currentConversationId || '',
            role: ROLES.USER,
            status: STATUSES.COMPLETED,
            content: inputValue,
            files: uploadedFiles.map(file => ({
                name: file.name,
                file_type: file.file_type,
                url: file.url
            })),
            json_content: {context},
            created_at: new Date().toISOString(),
            event_type: EVENT_TYPES.USER_MESSAGE,
            agent_type: AGENT_TYPE.AGENT_MODE
        });

        // 标记 agent 正在执行
        setIsGenerating(true);
        try{
            let newConversationId = currentConversationId;

            if (!newConversationId || newConversationId === 'temp') {
                // 创建新会话
                const res = await agentService.createConversation({title: inputValue});
                // 添加到会话历史
                setHistory(prev => [res, ...prev]);
                // 重命名消息键从 temp 到新的 conversationId
                setMessages(msgs => {
                    if (msgs['temp']) {
                        const { temp, ...rest } = msgs;
                        return {
                            ...rest,
                            [res.id]: temp
                        };
                    }
                    return msgs;
                });
                newConversationId = res.id;
                setCurrentConversationId(res.id);
            }

            const dashboardContext = context?.find(c => c.type === 'dashboard');
            // If there's a file in context, use its ID as file_id. 
            // If context file exists, it might override pulseId or work alongside. 
            // Assuming context file ID is the file_id we want to send.
            const fileContext = context?.find(c => c.type === 'file');
            const fileId = fileContext?.payload?.pulseId || pulseId;

            try {
                // 发送消息
                await agentService.sendMessage(
                    newConversationId!,
                    {
                        role: 'user',
                        content: inputValue,
                        files: uploadedFiles.filter(file => !(file instanceof File)).map(file => file as FileItem),
                        dashboard_data: dashboardContext?.payload,
                        file_id: fileId,
                        is_retry: isRetry
                    },
                    async (data: any) => {
                        // 直接使用接收到的 data 作为消息
                        addMessage(data, newConversationId);
                    },
                    (err: any) => {
                        throw err;
                    },
                    signal
                );
            } catch (e: any) {
                addMessage({
                    conversation_id: newConversationId!,
                    role: ROLES.ASSISTANT,
                    status: STATUSES.FAILED,
                    content: e.message,
                    json_content: {
                        type: 'error',
                        error: e.message
                    },
                    created_at: new Date().toISOString(),
                    event_type: EVENT_TYPES.ERROR,
                    agent_type: AGENT_TYPE.AGENT_MODE
                });
            }
        }catch(e: any){
            console.warn("error", e)
            // 如果不是取消操作，显示错误提示
            if (e.name !== 'AbortError') {
                showToast('Network error, please try again later.', 'error')
            }
        }finally{
            // 标记 agent 执行结束
            setIsGenerating(false);
        }
    }, [
        user,
        openSignInModal,
        addMessage,
        setMessages,
        chatSideBarScrollRef,
        showToast,
        currentConversationId,
        setIsGenerating,
        setCurrentConversationId,
        createNewAbortController,
        pulseId,
        setHistory
    ]);

    const retry = useCallback(() => {
        if (!currentConversationId) return;
        
        // 获取当前会话的消息
        setMessages(msgs => {
            const conversationMessages = msgs[currentConversationId] || [];
            
            // 找到最新的 USER_MESSAGE
            let lastUserMessageIndex = -1;
            for (let i = conversationMessages.length - 1; i >= 0; i--) {
                if (conversationMessages[i].event_type === EVENT_TYPES.USER_MESSAGE 
                    && conversationMessages[i].role === ROLES.USER) {
                    lastUserMessageIndex = i;
                    break;
                }
            }
            
            if (lastUserMessageIndex === -1) {
                console.warn('No user message found for retry');
                return msgs;
            }
            
            // 获取最新的 userMessage
            const lastUserMessage = conversationMessages[lastUserMessageIndex] as UserMessage;
            
            // 删除最新的 userMessage 及之后的所有消息
            const updatedMessages = conversationMessages.slice(0, lastUserMessageIndex);
            
            // 使用最新的 userMessage 重新发起 handleSearch
            setTimeout(() => {
                _handleSearch(
                    lastUserMessage.content, 
                    lastUserMessage.files || [], 
                    lastUserMessage.json_content?.context,
                    true
                );
            }, 0);
            
            return {
                ...msgs,
                [currentConversationId]: updatedMessages
            };
        });
    }, [currentConversationId, _handleSearch, setMessages]);

    useEffect(() => {
        if (user && inputValue) {
            if (inputValue) {
                _handleSearch(inputValue, inputFiles ? JSON.parse(inputFiles) : []);
            }
        }
    }, [user, inputValue, inputFiles, _handleSearch]);



    return {
        handleSearch: (content: string, files: FileItem[], context?: ContextItem[]) => _handleSearch(content, files, context, false),
        chatSideBarScrollRef,
        cancelCurrentRequest,
        retry
    };
};